import { FC, useEffect, useMemo, useState } from 'react';
import { useAtom, useSetAtom } from 'jotai';
import searchAtomPair from 'atoms/search-atom';
import { DataTable, type DataTableSelectionState } from 'components/data-table';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import type {
  PickerRow,
  PickerSearchParams,
  RecordKindConfig,
} from './record-kind-configs';
import styles from './link-document-panel.module.scss';

// Searchable, paginated, multi-select table for the Link panel's record kinds. Selection
// state is owned by the panel (survives paging + kind switches); this only reports toggles.

// Narrowed: the external title support picker is not a record link — no targetType, no accordion fields.
export type PickerTableConfig = Pick<
  RecordKindConfig,
  'href' | 'columns' | 'getRowId' | 'searchPlaceholder' | 'extraParams'
>;

interface Query {
  text: string;
  page: number;
  pageSize: number;
}

interface Props {
  config: PickerTableConfig;
  selectedIds: Set<string>;
  onToggle: (row: PickerRow) => void;
}

export const RecordPickerTable: FC<Props> = ({
  config,
  selectedIds,
  onToggle,
}) => {
  // A fresh atom pair per mount so switching kinds (keyed remount) starts clean.
  const { searchAtom, resetSearchDataAtom } = useMemo(
    () => searchAtomPair<PickerRow, PickerSearchParams>({ href: config.href }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [searchState, runSearch] = useAtom(searchAtom);
  const resetSearch = useSetAtom(resetSearchDataAtom);

  const [query, setQuery] = useState<Query>({
    text: '',
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // Runs the initial (empty-text) search on mount, then every time the debounced
  // search text or the page changes.
  useEffect(() => {
    runSearch(() => ({
      text: query.text,
      page: query.page,
      pageSize: query.pageSize,
      paginated: true,
      ...config.extraParams,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  useEffect(
    () => () => resetSearch({ unsetCurrentPayload: true }),
    [resetSearch]
  );

  // Memoized so an empty result set doesn't hand DataTable a fresh array (and a
  // fresh row model) on every render.
  const results = useMemo(
    () => (searchState.data?.results ?? []) as PickerRow[],
    [searchState.data]
  );
  const totalCount = searchState.data?.totalCount ?? results.length;

  // The panel owns the full selection; DataTable only needs the slice covering
  // the rows currently on screen.
  const selection = useMemo<DataTableSelectionState>(
    () => ({
      selectAll: false,
      toggledNodes: results
        .map((row) => config.getRowId(row))
        .filter((id) => selectedIds.has(id)),
    }),
    [results, selectedIds, config]
  );

  // Report whatever changed on this page back as individual toggles.
  const handleSelectionChange = (next: DataTableSelectionState) => {
    const reported = new Set(next.toggledNodes);
    results.forEach((row) => {
      const id = config.getRowId(row);
      const isSelected = next.selectAll ? !reported.has(id) : reported.has(id);
      if (isSelected !== selectedIds.has(id)) onToggle(row);
    });
  };

  // DataTable debounces the search box for us; a new term restarts at page 1.
  const handleSearchChange = (text: string) =>
    setQuery((q) => (q.text === text ? q : { ...q, text, page: 1 }));

  const handlePageChange = (page: number, pageSize: number) =>
    setQuery((q) => ({ ...q, page, pageSize }));

  return (
    <div className={styles.pickerTable}>
      <DataTable<PickerRow>
        data={results}
        columns={config.columns}
        getRowId={(row) => config.getRowId(row)}
        isLoading={searchState.loading}
        error={searchState.error ?? null}
        searchPlaceholder={config.searchPlaceholder}
        scroll={{ mode: 'paginated', serverDriven: true, totalCount }}
        controlledState={{
          globalFilter: query.text,
          pagination: { page: query.page, pageSize: query.pageSize },
          selection,
        }}
        onGlobalSearchChange={(s) => handleSearchChange(s.globalFilter)}
        onPaginationChange={(s) => handlePageChange(s.page, s.pageSize)}
        onSelectionChange={(s) => handleSelectionChange(s.selection)}
        highlightedRowIds={selectedIds}
        config={{
          // Search + checkboxes only — the picker is a linking surface, not a catalog,
          // and other toolbar controls lack server support. No select-all (post one row at a time).
          selectionType: 'multi',
          isBulkSelectEnabled: false,
          sorting: false,
          columnFilters: false,
          columnVisibility: false,
          columnReordering: false,
          columnResizing: false,
          columnPinning: false,
        }}
      />
    </div>
  );
};
